/**
 * VÉRAULT Portal — Confirmations Progress (unified confirmation model)
 *
 * Single source of truth for every confirmation surface: required
 * questionnaire answers + every published flight journey as an
 * independent approval item. Computed live by the backend and
 * refreshed on autosave / approval events.
 */
import React from "react";
import { CheckCircle2, Circle, MessageCircle } from "lucide-react";
import { Surface, api } from "../index";

const STATUS_META = {
  completed: { icon: CheckCircle2, cls: "text-[hsl(var(--accent-gold))]", label: "Approved" },
  pending: { icon: Circle, cls: "text-[hsl(var(--foreground-muted))]/50", label: "Awaiting review" },
  revision_requested: { icon: MessageCircle, cls: "text-[hsl(var(--accent-terracotta))]", label: "Revision requested" },
};

export function useConfirmationsSummary(endpoint, refreshKey = 0) {
  const [data, setData] = React.useState(null);
  const load = React.useCallback(() => {
    api.get(endpoint)
      .then((r) => setData(r.data))
      .catch(() => setData(null));
  }, [endpoint]);
  React.useEffect(() => { load(); }, [load, refreshKey]);
  React.useEffect(() => {
    const h = () => load();
    window.addEventListener("vt:smart-answers:refresh", h);
    window.addEventListener("vt:confirmations:refresh", h);
    return () => {
      window.removeEventListener("vt:smart-answers:refresh", h);
      window.removeEventListener("vt:confirmations:refresh", h);
    };
  }, [load]);
  return data;
}

export default function ConfirmationsProgress({ endpoint, refreshKey = 0, compact = false, advisor = false, testIdBase = "confirmations-progress" }) {
  const data = useConfirmationsSummary(endpoint, refreshKey);

  if (!data || !data.total) return null;
  const pct = Math.round((data.completed / data.total) * 100);
  const awaiting = data.awaiting_client ?? data.remaining;

  return (
    <Surface variant={compact ? "inset" : "default"} padded className="!p-4" data-testid={testIdBase}>
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--accent-gold))]">
          Confirmations & Decisions
        </p>
        <p className="font-serif text-[15px] text-[hsl(var(--foreground))]" data-testid={`${testIdBase}-count`}>
          {data.completed} of {data.total} completed
        </p>
        {awaiting > 0 && (
          <p className="font-accent italic text-[12px] text-[hsl(var(--foreground-muted))]" data-testid={`${testIdBase}-remaining`}>
            {awaiting} awaiting {advisor ? "the client" : "you"}
          </p>
        )}
        {data.revision_requested > 0 && (
          <p className="font-accent italic text-[12px] text-[hsl(var(--accent-terracotta))]" data-testid={`${testIdBase}-revisions`}>
            {data.revision_requested} revision request{data.revision_requested === 1 ? "" : "s"} {advisor ? "with you" : "with your advisor"}
          </p>
        )}
      </div>
      {(data.flights_total > 0 || data.decisions_total > 0) && (
        <p className="mt-1 font-mono text-[9.5px] uppercase tracking-[0.16em] text-[hsl(var(--foreground-muted))]" data-testid={`${testIdBase}-breakdown`}>
          {data.questions_completed} of {data.questions_total} profile confirmations
          {data.flights_total > 0 && ` · ${data.flights_completed} of ${data.flights_total} flight decisions`}
          {data.decisions_total > 0 && ` · ${data.decisions_completed} of ${data.decisions_total} proposal decisions`}
        </p>
      )}
      {advisor && (data.ready_for_travel_ops > 0 || data.approved_blocked > 0) && (
        <p className="mt-1 font-mono text-[9.5px] uppercase tracking-[0.16em]" data-testid={`${testIdBase}-readiness`}>
          <span className="text-emerald-700">{data.ready_for_travel_ops || 0} ready for Travel Operations</span>
          <span className="text-[hsl(var(--foreground-muted))]"> · </span>
          <span className="text-red-700">{data.approved_blocked || 0} approved but blocked</span>
        </p>
      )}
      <div className="mt-2 h-1 rounded-full bg-[rgba(0,0,0,0.05)] overflow-hidden">
        <div
          className="h-full bg-[hsl(var(--accent-gold))]/70 transition-all duration-500"
          style={{ width: `${pct}%` }}
          aria-hidden
        />
      </div>
      {!compact && (
        <ul className="mt-3 space-y-1.5" data-testid={`${testIdBase}-items`}>
          {data.items.map((it) => {
            const meta = STATUS_META[it.status] || STATUS_META.pending;
            const Icon = meta.icon;
            return (
              <li key={`${it.kind}-${it.id}`} data-testid={`${testIdBase}-item-${it.id}`}>
                <div className="flex items-center gap-2">
                  <Icon className={`size-3.5 shrink-0 ${meta.cls}`} strokeWidth={1.5} />
                  <span className="font-serif text-[13px] text-[hsl(var(--foreground))] truncate">{it.label}</span>
                  {it.updated_at && (
                    <span className="font-mono text-[8.5px] text-[hsl(var(--foreground-muted))] shrink-0">
                      {new Date(it.updated_at).toLocaleDateString("en-US")}
                    </span>
                  )}
                  <span className="ml-auto font-mono text-[8.5px] uppercase tracking-[0.14em] text-[hsl(var(--foreground-muted))] shrink-0">
                    {meta.label}
                  </span>
                </div>
                {it.answer_display && (
                  <p className="ml-[22px] font-accent italic text-[12px] text-[hsl(var(--foreground-muted))] leading-relaxed" data-testid={`${testIdBase}-item-${it.id}-answer`}>
                    {it.answer_display}
                  </p>
                )}
                {it.note && (
                  <p className="ml-[22px] font-accent italic text-[12px] text-[hsl(var(--accent-terracotta))] leading-relaxed" data-testid={`${testIdBase}-item-${it.id}-note`}>
                    “{it.note}”
                  </p>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </Surface>
  );
}
